import { useState, type FC } from 'react';
import { Button, Input } from '@selfinspectionorg/ui-kit';
import styles from './Mfa.module.scss';
import { Card } from '@/components/ui';
import { useMfaVerifyMutation, type MfaVerifyResponse } from '@/shared/api/auth';
import { getLoginFlowError, isMfaSessionExpiredError } from '@/shared/utils';

interface Props {
  /** Receives the raw dual-outcome verify result; the container adopts a session or captures a proof. */
  onSuccess: (view: MfaVerifyResponse) => void;
  mfaPending: string;
  onExpired: () => void;
  onBack: () => void;
}

// `MfaCodeDto` is `Length(6, 32)`, so the same field accepts a 6-digit TOTP or an 11-char backup
// code (`xxxxx-xxxxx`). It is a plain text input rather than a fixed OTP grid for that reason.
const MIN_CODE_LENGTH = 6;

export const CodeVerify: FC<Props> = ({ mfaPending, onSuccess, onExpired, onBack }) => {
  const { verify, isVerifying } = useMfaVerifyMutation();

  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);

    try {
      const view = await verify({ mfaPending, code: code.trim() });
      onSuccess(view);
    } catch (caught) {
      if (isMfaSessionExpiredError(caught)) {
        onExpired();

        return;
      }

      setError(getLoginFlowError(caught));
    }
  };

  return (
    <>
      <Card.BackButton onClick={onBack} testId="mfa-back-button" />
      <Card.Title
        title="Enter your code"
        description={
          useBackupCode
            ? 'Enter one of the backup codes you saved when setting up two-factor authentication.'
            : 'Enter the 6-digit code from your authenticator app.'
        }
      />
      <div className={styles.body}>
        <Input
          label={useBackupCode ? 'Backup code' : 'Authenticator code'}
          name="code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder={useBackupCode ? 'xxxxx-xxxxx' : '123456'}
          errored={!!error}
          helperMessage={error || undefined}
          autoFocus
          testId="mfa-verify-code-input"
        />
        <Button
          width="100%"
          onClick={submit}
          disabled={code.trim().length < MIN_CODE_LENGTH || isVerifying}
          isLoading={isVerifying}
          data-testid="mfa-verify-button"
        >
          Verify
        </Button>
        <button
          type="button"
          className={styles.resendButton}
          onClick={() => {
            setUseBackupCode((value) => !value);
            setCode('');
            setError(null);
          }}
          data-testid="mfa-verify-toggle-backup"
        >
          {useBackupCode ? 'Use an authenticator code instead' : 'Use a backup code instead'}
        </button>
      </div>
    </>
  );
};
